/**
 * Copyright (c) 2021 - Present. Blend Labs, Inc. All rights reserved
 * Blend Confidential - Restricted
 */
import React, { useEffect } from 'react';
import { useState } from 'react';
import { useLocation, useHistory } from 'react-router';
import { css, StyleSheet } from 'aphrodite';
import { ActionButton, BrandColors } from '@blend/enterprise-components';
import { convertURLSearchToFilterParams, convertFilterParamsToURLSearch } from '../../../../utils/filters';
import { clearedPipelineFilters } from '../../../../utils/filters';
import { usePermissions } from '../../../../../src/hooks/usePermissions';
import { NotaryAPI } from '../../../../../src/api/notary/types';
import { FilterDivider } from './FilterDivider';
import { FilterCheckbox } from './FilterCheckbox';
import { MultiSelectFilter } from './MultiSelectFilter';
import { FilterParams } from '@shared/types/pipeline';
import {
  AssignmentStatus,
  RoomStatus,
  AppointmentStatus,
  LenderDocuments,
  TitleDocuments,
} from '@shared/constants/room';
import { TypeaheadMultiSelect } from 'src/components/TypeaheadMultiSelect/TypeaheadMultiSelect';

const ALL_OPTION_DO_NOT_USE = 'ALL_OPTION_DO_NOT_USE';
export const ALL_ORGANIZATIONS_SUGGESTION: TypeaheadMultiSelect.Suggestion = {
  value: ALL_OPTION_DO_NOT_USE,
  display: 'All Organizations',
  exclusive: true,
};

export const ALL_GROUPS_SUGGESTION: TypeaheadMultiSelect.Suggestion = {
  value: 'All Groups',
  display: 'All Groups',
  exclusive: true,
};

export namespace RoomsPipelineFilterContent {
  export interface Props {
    closePopup: () => void;
    allOrganizations: string[];
    allGroups: string[];
  }
}

const styles = StyleSheet.create({
  filtersPopover: {
    display: 'flex',
    flexDirection: 'column',
    padding: '16px',
    paddingBottom: 0,
    minWidth: '400px',
    gap: '20px',
    maxHeight: 600,
    overflowY: 'auto',
  },
  hr: {
    borderTop: `1px solid ${BrandColors.CLOUD_50}`,
  },
  inputErrorContainer: {
    marginTop: '8px',
  },
  sectionHeaderAndTextfieldWrapper: {
    display: 'flex',
    flexDirection: 'column',
    gap: '12px',
  },
  checkboxContainer: {
    display: 'grid',
    gridTemplateColumns: 'auto auto',
    gap: '8px 16px',
  },
  actionButtonsRow: {
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'flex-end',
    gap: '8px',
    position: 'sticky',
    bottom: 0,
    background: 'white',
    padding: '16px 0',
  },
});

type KeysMatching<T extends object, V> = {
  [K in keyof T]-?: T[K] extends V ? K : never;
}[keyof T];

export const RoomsPipelineFilterContent: React.FC<RoomsPipelineFilterContent.Props> = ({
  closePopup,
  allOrganizations,
  allGroups,
}) => {
  const history = useHistory();
  const location = useLocation();
  const hasSuperAdminPermission = usePermissions().includes(NotaryAPI.BSRPermissions.BSRSuperAdmin);
  const [filterUpdates, setFilterUpdates] = useState<FilterParams>(
    convertURLSearchToFilterParams(location.search, hasSuperAdminPermission),
  );

  const [organizationSelections, setOrganizationSelections] = useState<TypeaheadMultiSelect.Selections>({});
  const [groupSelections, setGroupSelections] = useState<TypeaheadMultiSelect.Selections>({});

  const saveFilterOptions = () => {
    const pathname = location.pathname.endsWith('/') ? location.pathname.slice(0, -1) : location.pathname;
    history.push(`${pathname}?${convertFilterParamsToURLSearch(filterUpdates, hasSuperAdminPermission)}`);
    closePopup();
  };

  const clearFilterOptions = () => {
    setFilterUpdates(clearedPipelineFilters(hasSuperAdminPermission));
    setOrganizationSelections({});
    const pathname = location.pathname.endsWith('/') ? location.pathname.slice(0, -1) : location.pathname;
    history.push(
      `${pathname}?${convertFilterParamsToURLSearch(
        clearedPipelineFilters(hasSuperAdminPermission),
        hasSuperAdminPermission,
      )}`,
    );
    closePopup();
  };

  const updateStatusFilter = (filter: RoomStatus) => (isChecked: boolean) => {
    let newStatuses = [...filterUpdates.status];
    if (isChecked) {
      newStatuses.push(filter);
    } else {
      newStatuses = filterUpdates.status.filter(status => status !== filter);
    }
    setFilterUpdates({
      ...filterUpdates,
      status: newStatuses,
    });
  };

  const updateAssignmentFilter = (filter: AssignmentStatus) => (isChecked: boolean) => {
    let newStatuses = [...filterUpdates.assignmentStatus];
    if (isChecked) {
      newStatuses.push(filter);
    } else {
      newStatuses = filterUpdates.assignmentStatus.filter(status => status !== filter);
    }
    setFilterUpdates({
      ...filterUpdates,
      assignmentStatus: newStatuses,
    });
  };

  const updateArrayFilterWithKey =
    <K extends KeysMatching<FilterParams, string[]>>(key: K, filter: FilterParams[K][number]) =>
    (isChecked: boolean) => {
      setFilterUpdates(prevFilterValues => {
        const prevValue = prevFilterValues[key];
        let newFilterValue = [...prevValue];
        if (isChecked) {
          newFilterValue.push(filter);
        } else {
          newFilterValue = newFilterValue.filter(status => status !== filter) as FilterParams[K]; // Type assertion when filtering array
        }

        const updatedFilterUpdates: FilterParams = {
          ...prevFilterValues,
          [key]: newFilterValue,
        };

        return updatedFilterUpdates;
      });
    };

  const updateScheduledDateFilter = (filter: string) => (isChecked: boolean) => {
    setFilterUpdates({
      ...filterUpdates,
      [filter]: isChecked,
    });
  };

  const updateOrganizationFilter = (selections: TypeaheadMultiSelect.Selections): void => {
    setOrganizationSelections(selections);
    const organizations = Object.keys(selections);
    const filteredOrganizations = organizations.filter(org => org !== ALL_OPTION_DO_NOT_USE);
    setFilterUpdates({
      ...filterUpdates,
      organizations: filteredOrganizations,
    });
  };

  const updateGroupFilter = (selections: TypeaheadMultiSelect.Selections): void => {
    setGroupSelections(selections);
    const selectionValues = Object.keys(selections);
    const groups = selectionValues.includes('All Groups') ? allGroups : selectionValues;
    setFilterUpdates({
      ...filterUpdates,
      groups,
    });
  };

  const updateAppointmentStatusFilter = (filter: AppointmentStatus) => (isChecked: boolean) => {
    let newAppointmentStatuses = [...filterUpdates.appointmentStatus];
    if (isChecked) {
      newAppointmentStatuses.push(filter);
    } else {
      newAppointmentStatuses = filterUpdates.appointmentStatus.filter(status => status !== filter);
    }
    setFilterUpdates({
      ...filterUpdates,
      appointmentStatus: newAppointmentStatuses,
    });
  };

  const buildDropdownOptions = (
    options: string[],
    allOption: TypeaheadMultiSelect.Suggestion,
  ): TypeaheadMultiSelect.Suggestion[] => {
    const suggestions = options.map(organization => ({
      display: organization,
      value: organization,
    }));
    suggestions.push(allOption);
    return suggestions;
  };

  const constructDropdownSelections = (selections: string[]): TypeaheadMultiSelect.Selections => {
    return selections.reduce((acc, org) => {
      return {
        ...acc,
        [org]: {
          display: org,
          value: org,
        },
      };
    }, {});
  };

  useEffect(() => {
    const selectedOrganizations = constructDropdownSelections(filterUpdates.organizations);
    const selectedGroups = constructDropdownSelections(filterUpdates.groups);
    setOrganizationSelections(selectedOrganizations);
    setGroupSelections(selectedGroups);
  }, [location.search]);

  return (
    <div className={css(styles.filtersPopover)} id="it-PipelineFilter-popover-content">
      {hasSuperAdminPermission && (
        <div className={css(styles.sectionHeaderAndTextfieldWrapper)}>
          <FilterDivider>Notary Group</FilterDivider>
          <MultiSelectFilter
            id="groups"
            options={buildDropdownOptions(allGroups, ALL_GROUPS_SUGGESTION)}
            onSelectionChange={updateGroupFilter}
            placeholder="Select group"
            selections={groupSelections}
          />
        </div>
      )}

      <div className={css(styles.sectionHeaderAndTextfieldWrapper)}>
        <FilterDivider>Organization</FilterDivider>
        <MultiSelectFilter
          id="organizations"
          options={buildDropdownOptions(allOrganizations, ALL_ORGANIZATIONS_SUGGESTION)}
          onSelectionChange={updateOrganizationFilter}
          placeholder="Select organization"
          selections={organizationSelections}
        />
      </div>

      <div className={css(styles.sectionHeaderAndTextfieldWrapper)}>
        <FilterDivider>Scheduled Date</FilterDivider>
        <div className={css(styles.checkboxContainer)}>
          <FilterCheckbox
            isChecked={filterUpdates.scheduledForToday}
            onChange={updateScheduledDateFilter('scheduledForToday')}
            tooltip="If selected, display all notarizations scheduled for today."
          >
            Today
          </FilterCheckbox>

          <FilterCheckbox
            isChecked={filterUpdates.scheduledUpcoming}
            onChange={updateScheduledDateFilter('scheduledUpcoming')}
            tooltip="If selected, display all notarizations scheduled but not yet started."
          >
            All upcoming
          </FilterCheckbox>

          <FilterCheckbox
            isChecked={filterUpdates.scheduledForTomorrow}
            onChange={updateScheduledDateFilter('scheduledForTomorrow')}
            tooltip="If selected, display all notarizations scheduled for tomorrow."
          >
            Tomorrow
          </FilterCheckbox>

          <FilterCheckbox
            isChecked={filterUpdates.scheduledInPast}
            onChange={updateScheduledDateFilter('scheduledInPast')}
            tooltip="If selected, display all notarizations scheduled prior to today."
          >
            All past
          </FilterCheckbox>

          <FilterCheckbox
            isChecked={filterUpdates.scheduledForYesterday}
            onChange={updateScheduledDateFilter('scheduledForYesterday')}
            tooltip="If selected, display all notarizations scheduled for yesterday."
          >
            Yesterday
          </FilterCheckbox>
        </div>
      </div>

      <div className={css(styles.sectionHeaderAndTextfieldWrapper)}>
        <FilterDivider>Status</FilterDivider>
        <div className={css(styles.checkboxContainer)}>
          <FilterCheckbox
            isChecked={filterUpdates.status.includes(RoomStatus.DRAFT)}
            onChange={updateStatusFilter(RoomStatus.DRAFT)}
            tooltip='If selected display all notarizations with a status of "Draft".'
          >
            Draft
          </FilterCheckbox>

          <FilterCheckbox
            isChecked={filterUpdates.status.includes(RoomStatus.PAUSED)}
            onChange={updateStatusFilter(RoomStatus.PAUSED)}
            tooltip='If selected display all notarizations with a status of "Paused".'
          >
            Paused
          </FilterCheckbox>

          <FilterCheckbox
            isChecked={filterUpdates.status.includes(RoomStatus.CREATED)}
            onChange={updateStatusFilter(RoomStatus.CREATED)}
            tooltip='If selected, display all notarizations with a status of "Created".'
          >
            Created
          </FilterCheckbox>

          <FilterCheckbox
            isChecked={filterUpdates.status.includes(RoomStatus.NEEDS_ATTENTION)}
            onChange={updateStatusFilter(RoomStatus.NEEDS_ATTENTION)}
            tooltip='If selected display all notarizations with a status of "Needs Attention".'
          >
            Needs attention
          </FilterCheckbox>

          <FilterCheckbox
            isChecked={filterUpdates.status.includes(RoomStatus.COMPLETED)}
            onChange={updateStatusFilter(RoomStatus.COMPLETED)}
            tooltip='If selected, display all notarizations with a status of "Completed".'
          >
            Completed
          </FilterCheckbox>

          <FilterCheckbox
            isChecked={filterUpdates.status.includes(RoomStatus.CANCELED)}
            onChange={updateStatusFilter(RoomStatus.CANCELED)}
            tooltip='If selected display all notarizations with a status of "Canceled".'
          >
            Canceled
          </FilterCheckbox>
        </div>
      </div>

      <div className={css(styles.sectionHeaderAndTextfieldWrapper)}>
        <FilterDivider>Assigned Notary</FilterDivider>
        <div className={css(styles.checkboxContainer)}>
          <FilterCheckbox
            isChecked={filterUpdates.assignmentStatus.includes(AssignmentStatus.DONE)}
            onChange={updateAssignmentFilter(AssignmentStatus.DONE)}
            tooltip="If selected display all notarizations with an assigned notary."
          >
            Done
          </FilterCheckbox>

          <FilterCheckbox
            isChecked={filterUpdates.assignmentStatus.includes(AssignmentStatus.PENDING)}
            onChange={updateAssignmentFilter(AssignmentStatus.PENDING)}
            tooltip="If selected display all notarizations that do not have an assigned notary."
          >
            Pending
          </FilterCheckbox>
        </div>
      </div>

      <div className={css(styles.sectionHeaderAndTextfieldWrapper)}>
        <FilterDivider>Appointment Time Status</FilterDivider>
        <div className={css(styles.checkboxContainer)}>
          <FilterCheckbox
            isChecked={!!filterUpdates.appointmentStatus.includes(AppointmentStatus.PENDING_PREFERENCES)}
            onChange={updateAppointmentStatusFilter(AppointmentStatus.PENDING_PREFERENCES)}
            tooltip="If selected display all notarizations that has Pending Preferences for appointment time status."
          >
            Pending preferences
          </FilterCheckbox>

          <FilterCheckbox
            isChecked={!!filterUpdates.appointmentStatus.includes(AppointmentStatus.TIME_CONFIRMED)}
            onChange={updateAppointmentStatusFilter(AppointmentStatus.TIME_CONFIRMED)}
            tooltip="If selected display all notarizations have a confirmed time."
          >
            Time confirmed
          </FilterCheckbox>

          <FilterCheckbox
            isChecked={!!filterUpdates.appointmentStatus.includes(AppointmentStatus.PENDING_CONFIRMATION)}
            onChange={updateAppointmentStatusFilter(AppointmentStatus.PENDING_CONFIRMATION)}
            tooltip="If selected display all notarizations that have a pending confirmation for appointment status."
          >
            Pending confirmation
          </FilterCheckbox>
        </div>
      </div>

      <div className={css(styles.sectionHeaderAndTextfieldWrapper)}>
        <FilterDivider>Lender Documents</FilterDivider>
        <div className={css(styles.checkboxContainer)}>
          <FilterCheckbox
            isChecked={!!filterUpdates.lenderDocuments.includes(LenderDocuments.INCOMPLETE_OR_MISSING)}
            onChange={updateArrayFilterWithKey('lenderDocuments', LenderDocuments.INCOMPLETE_OR_MISSING)}
            tooltip="If selected display all notarizations that has incomplete or missing for lender documents."
          >
            Incomplete/missing
          </FilterCheckbox>

          <FilterCheckbox
            isChecked={!!filterUpdates.lenderDocuments.includes(LenderDocuments.PENDING_TAGGING)}
            onChange={updateArrayFilterWithKey('lenderDocuments', LenderDocuments.PENDING_TAGGING)}
            tooltip="If selected display all notarizations that has lender documents with pending tagging."
          >
            Pending tagging
          </FilterCheckbox>

          <FilterCheckbox
            isChecked={!!filterUpdates.lenderDocuments.includes(LenderDocuments.TAGGED)}
            onChange={updateArrayFilterWithKey('lenderDocuments', LenderDocuments.TAGGED)}
            tooltip="If selected display all notarizations that that has tagged for lender documents."
          >
            Tagged
          </FilterCheckbox>
        </div>
      </div>

      <div className={css(styles.sectionHeaderAndTextfieldWrapper)}>
        <FilterDivider>Title Documents</FilterDivider>
        <div className={css(styles.checkboxContainer)}>
          <FilterCheckbox
            isChecked={!!filterUpdates.titleDocuments.includes(TitleDocuments.PENDING_DOCS)}
            onChange={updateArrayFilterWithKey('titleDocuments', TitleDocuments.PENDING_DOCS)}
            tooltip="If selected display all notarizations that has pending title documents."
          >
            Pending docs
          </FilterCheckbox>

          <FilterCheckbox
            isChecked={!!filterUpdates.titleDocuments.includes(TitleDocuments.PENDING_TAGGING)}
            onChange={updateArrayFilterWithKey('titleDocuments', TitleDocuments.PENDING_TAGGING)}
            tooltip="If selected display all notarizations that has pending tagging title documents."
            name="title-documents-pending-tagging"
          >
            Pending tagging
          </FilterCheckbox>

          <FilterCheckbox
            isChecked={!!filterUpdates.titleDocuments.includes(TitleDocuments.RECEIVED_AND_TAGGED)}
            onChange={updateArrayFilterWithKey('titleDocuments', TitleDocuments.RECEIVED_AND_TAGGED)}
            tooltip="If selected display all notarizations that has received and tagged title documents."
          >
            Received & tagged
          </FilterCheckbox>
        </div>
      </div>

      <div className={css(styles.actionButtonsRow)}>
        <ActionButton
          data-dd-privacy="allow"
          theme="secondary"
          btnSize="sm"
          id="it-PipelineFilter-clearFilters-btn"
          label="Clear all"
          onClick={clearFilterOptions}
        />

        <ActionButton
          data-dd-privacy="allow"
          btnSize="sm"
          id="it-PipelineFilter-applyFilters-save-btn"
          label="Apply"
          onClick={saveFilterOptions}
        />
      </div>
    </div>
  );
};
